<script lang="ts" setup>
import {
  useAllVisibility,
  useDefaultAvatar,
  usePreviewImg,
} from "@/use/common";
import {
  TextEllipsis,
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import UserAvatar from "@/components/UserAvatar.vue";
import { computed } from "vue";
import { watch } from "vue";
import { StoryBookInfoEditExpose } from "../story-book/StoryBookInfoEdit.vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStoryBook } from "@/stores/story-book";
import { getWordCount } from "@/modules/editor/editor-json-utils";
import { useStory } from "@/stores/story";
import { useRouter } from "vue-router";
import { useReply } from "@/stores/reply";
import { StoryBook, Reply } from "@/types/entity";
import { Collection } from "@/types/collection";
const { t } = useI18n();
const storyBookStore = useStoryBook();
const storyStore = useStory();
const router = useRouter();
const replyStore = useReply();
const props = defineProps<{
  storyBook: StoryBook;
  isOwner: boolean;
}>();

const emit = defineEmits<{
  (e: "onCardClick"): void;
}>();
const VISIBILITIES = useAllVisibility();
const visibilityText = computed(() => {
  return VISIBILITIES.find((v) => v.value === props.storyBook!.visibility)
    ?.text;
});
const storyList = computed(() => {
  if (
    !props.storyBook ||
    !storyStore.storyBookId2StoryUuids[props.storyBook!.id!]
  ) {
    return [];
  }
  return storyStore.storyBookId2StoryUuids[props.storyBook!.id!].map(
    (uuid) => storyStore.storyMap[uuid]
  );
});
const totalWordCount = computed(() => {
  return storyList.value.reduce((acc, cur) => {
    const json = storyStore.getJson(cur.text);
    return acc + getWordCount(json);
  }, 0);
});
const totalReactionCount = computed(() => {
  const storyCount = storyList.value.reduce((acc, cur) => {
    return (
      acc +
      cur.reply?.interactivityEmoji?.reduce((acc, cur) => acc + cur.amount, 0)
    );
  }, 0);
  return (
    storyCount +
    props.storyBook!.reply.interactivityEmoji?.reduce((acc, cur) => {
      return acc + cur.amount;
    }, 0)
  );
});
const totalViewCount = computed(() => {
  return storyList.value.reduce((acc, cur) => {
    return acc + ((cur.reply as Reply)?.viewerCount || 0);
  }, 0);
});
const totalReplyCount = computed(() => {
  return replyStore.countOwnerMap[props.storyBook?.id!]?.replyCount || 0;
  // const storyCount = storyList.value.reduce((acc, cur) => {
  //   return acc + cur.reply.replyCount;
  // }, 0);
  // return storyCount + props.storyBook!.reply.replyCount;
});

const showEditPopup = ref(false);
const storyBookInfoEditRef = ref<StoryBookInfoEditExpose | null>(null);
watch(showEditPopup, (show) => {
  if (show) {
    storyBookInfoEditRef.value?.reset();
  }
});
const gradientStyle = "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))";
const bannerStyle = computed(() => {
  const bannerUrl = props.storyBook?.media?.url;
  if (bannerUrl) {
    return {
      filter: "blur(3px)",
      background: `${gradientStyle}, url(${bannerUrl}) no-repeat`,
      backgroundSize: "cover",
    };
  } else {
    return {
      filter: "blur(3px)",
      backgroundImage:
        "linear-gradient(45deg, rgb(81 7 115) 0%, rgb(196, 58, 48) 100%)",
      backgroundSize: "cover",
    };
  }
});

const onDelete = async () => {
  try {
    await showConfirmDialog({
      title: t("story.delete"),
      message: t("story.deleteDesc"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
    const loadingToast = showLoadingToast({
      message: t("Message.deletingStory"),
      duration: 0,
    });
    try {
      await storyBookStore.deleteStoryBook(props.storyBook!);
      loadingToast.close();
      showSuccessToast(t("Message.deleteSuccessful"));
      router.push({ name: "storyList" });
    } finally {
      loadingToast.close();
    }
  } catch {
    // Cancel => Do nothing
  }
};

const selectCollectionIdList = ref<number[]>([]);
const selectCollectionList = computed<Collection[]>(() => {
  if (!props.storyBook.collectionzz) return [];
  selectCollectionIdList.value = props.storyBook.collectionzz.map((c) => c.id!);
  return props.storyBook.collectionzz;
});
const onConfirmCollection = async (idList: number[]) => {
  selectCollectionIdList.value = idList;
  const loading = showLoadingToast({
    message: t("Message.saving"),
    duration: 0,
  });
  await storyBookStore.updateStoryBook(props.storyBook.id!, {
    collectionIdzz: idList,
  });
  loading.close();
};
</script>
<template>
  <div>
    <div class="relative flex flex-col overflow-hidden">
      <div
        class="flex justify-center items-center gap-4 relative z-10"
        @click="emit('onCardClick')"
      >
        <div class="relative flex">
          <VanImage
            :src="
              storyBook?.media?.url
                ? usePreviewImg(storyBook.media.url)
                : useDefaultAvatar()
            "
            alt=""
            class="h-[206px] w-[206px] bg-primary"
            fit="cover"
            crossorigin
          />
          <div
            v-if="!storyBook?.dtPublish"
            class="absolute right-0 top-1 badge"
          >
            {{ $t("story.draft") }}
          </div>
          <div
            v-else-if="storyBook!.visibility !== 1"
            class="absolute right-0 top-1 badge"
          >
            {{ visibilityText }}
          </div>
        </div>
        <div
          v-if="isOwner"
          class="absolute right-1 bottom-1.5 p-1 rounded flex gap-1"
        >
          <button
            class="btn btn-xs flex items-center h-9"
            @click="showEditPopup = true"
          >
            <i class="iconify mingcute--edit-3-line" />
            <!-- <span>编辑故事基本信息</span> -->
          </button>
          <button class="btn btn-xs flex items-center h-9" @click="onDelete">
            <i class="iconify mingcute--delete-2-line" />
          </button>
        </div>
      </div>
      <div
        :style="bannerStyle"
        class="absolute top-0 bottom-0 left-0 right-0 z-0"
      />
    </div>

    <div class="flex flex-col gap-3 flex-grow overflow-hidden p-2.5">
      <div
        class="text-base font-bold overflow-hidden whitespace-nowrap overflow-ellipsis"
      >
        {{ storyBook?.title }}
      </div>
      <div
        class="flex gap-2 items-center w-full overflow-hidden cursor-pointer"
        @click="
          router.push({
            name: 'mateHome',
            params: { uuid: storyBook?.mate.uuid },
          })
        "
      >
        <user-avatar
          :avatar-url="storyBook?.mate.mediaUrl"
          class="h-6 shrink-0"
        />
        <div class="text-base-content/50 w-full text-overflow-ellipsis">
          {{ storyBook?.mate.name }}
        </div>
      </div>
      <div class="text-base-content/50" @click="$event.stopPropagation()">
        <text-ellipsis
          :collapse-text="$t('Buttons.collapse')"
          :content="storyBook?.description"
          :expand-text="$t('Buttons.expand')"
          class="text-base-content/50 story-desc"
          rows="2"
        />
      </div>
    </div>

    <div
      class="flex items-center justify-around p-2 m-2 mb-0.5 text-base-content/50 border rounded"
    >
      <div class="flex flex-col items-center justify-center">
        <span class="text-primary font-bold"> {{ totalViewCount }} </span>
        <span>{{ $t("story.read") }}</span>
      </div>
      <div class="flex flex-col items-center justify-center">
        <span class="text-primary font-bold"> {{ totalWordCount }} </span>
        <span>{{ $t("story.wordCount") }}</span>
      </div>
      <div class="flex flex-col items-center justify-center">
        <span class="text-primary font-bold"> {{ totalReactionCount }} </span>
        <span>{{ $t("story.like") }}</span>
      </div>
      <div class="flex flex-col items-center justify-center">
        <span class="text-primary font-bold"> {{ totalReplyCount }} </span>
        <span>{{ $t("Overview.comments") }}</span>
      </div>
    </div>

    <div v-if="isOwner">
      <modal-select-collection
        :selected="selectCollectionIdList"
        @confirm="onConfirmCollection"
      />
    </div>
    <div v-if="selectCollectionList.length > 0 && !isOwner">
      <div class="flex justify-between items-center p-4">
        <div class="flex items-center gap-2">
          <i class="iconfont icon-collection text-primary" />
          <div class="flex flex-wrap gap-1">
            <span
              v-for="(c, idx) in selectCollectionList"
              :key="c.uuid"
              class="text-primary cursor-pointer"
              @click="
                router.push({
                  name: 'collectionMain',
                  params: { uuid: c.uuid },
                })
              "
            >
              {{ c.title }}
              <span v-if="idx < selectCollectionList.length - 1">, </span>
            </span>
          </div>
        </div>
        <div v-if="isOwner">
          <i class="iconfont icon-i-right"></i>
        </div>
      </div>
    </div>

    <!-- <rank-status-of-entity
      :item="storyBook"
      :type="CoinBalanceRecordEntityType.StoryBook"
      class="mx-2"
    /> -->
  </div>

  <VanPopup
    v-if="storyBook"
    v-model:show="showEditPopup"
    class="w-[350px] py-10"
    position="center"
    round
  >
    <story-book-info-edit
      ref="storyBookInfoEditRef"
      :description="storyBook.description"
      :media="storyBook.media"
      :story-book-id="storyBook.id"
      :title="storyBook.title"
      :visibility="storyBook.visibility"
      @on-success="showEditPopup = false"
      @on-cancel="showEditPopup = false"
    />
  </VanPopup>
</template>
<style>
.story-desc .van-text-ellipsis__action {
  @apply text-base-content/70;
}
</style>
